import { prefixOptions, splitPhoneNumber, isValidPhone } from 'azshared';
import React, { useCallback, useMemo } from 'react';
import Select from 'react-select';
import { AppInputText } from '../input-text/app-input-text';

const webPrefixOptions = prefixOptions.map(opt => ({
  value: opt.prefix,
  label: opt.prefix,
  code: opt.code,
  name: opt.name
}));

const reactSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    height: '32px',
    minHeight: '32px',
    borderRadius: '20px',
    borderColor: '#D8DAE5',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#D8DAE5',
    },
    width: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: '30px',
    padding: '0 0 0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden'
  }),
  singleValue: (provided: any) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    padding: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '30px',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: '2px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    width: '100px',
    zIndex: 9999
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#EBF0FF' : state.isFocused ? '#F3F6FF' : 'transparent',
    padding: '6px 12px',
    cursor: 'pointer'
  })
};

const formatOptionLabel = (option: any) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ fontSize: '13px', color: '#2D3748', fontWeight: 500 }}>
      {option.label}
    </span>
  </div>
);

interface SearchablePrefixPickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  valid?: boolean;
  onCopy?: (e: any) => void;
  onPaste?: (e: any) => void;
}

export const SearchablePrefixPicker = ({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  valid,
  onCopy,
  onPaste
}: SearchablePrefixPickerProps) => {
  const { prefix, number } = useMemo(() => {
    return splitPhoneNumber(value);
  }, [value]);

  const selectedPrefixOption = useMemo(() => {
    return webPrefixOptions.find(opt => opt.value === prefix) || null;
  }, [prefix]);

  const handlePrefixChange = useCallback((selectedOpt: any) => {
    if (selectedOpt) {
      onChange(selectedOpt.value + number);
    }
  }, [number, onChange]);

  const handleNumberChange = useCallback((newNumber: string | undefined) => {
    const cleanNumber = (newNumber || '').replace(/\D/g, '');
    onChange(prefix + cleanNumber);
  }, [prefix, onChange]);

  const isPhoneValid = useMemo(() => {
    if (valid !== undefined) return valid;
    return isValidPhone(value);
  }, [value, valid]);

  return (
    <div className='phone-number-input-row'>
      <div className='prefix-select-container'>
        <Select
          options={webPrefixOptions}
          value={selectedPrefixOption}
          onChange={handlePrefixChange}
          styles={reactSelectStyles}
          formatOptionLabel={formatOptionLabel}
          isSearchable
          isDisabled={disabled}
        />
      </div>
      <div className='number-input-container'>
        <AppInputText
          placeholder={placeholder}
          value={number}
          onChange={handleNumberChange}
          onCopy={onCopy}
          onPaste={onPaste}
          valid={isPhoneValid}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
